/** F-115 — Single source of truth for user-facing labels (frontend mirror).
 *
 *  This file MUST stay in sync with `/app/backend/app/labels.py`. A pytest
 *  guard `test_labels_sync.py` fails CI if the two diverge on any key or
 *  human-visible string.
 *
 *  Every chip/ribbon/JSON-LD/OG surface in the frontend imports from here.
 *  Never hard-code "DIRECT" / "MODEL" / etc. in a page component.
 */

// Data-source chip labels — small pills next to a number
export const DATA_SOURCE_LABELS = {
  DIRECT: {
    label: "DIRECT",
    tone: "success",
    helper: "First-party device count from the broadcaster's stream metadata.",
    long: "Real-time listener count read from the broadcaster's Icecast/SHOUTcast stream metadata. Most reliable tier.",
  },
  PANEL: {
    label: "PANEL",
    tone: "success",
    helper: "Panel-corrected estimate anchored to a representative sample (N ≥ 400).",
    long: "Audience estimate derived from a representative respondent panel, weighted to GSS 2021 PHC + GLSS-7 anchors.",
  },
  HYBRID: {
    label: "HYBRID",
    tone: "info",
    helper: "DIRECT device count fused with PANEL demographics for weighting.",
    long: "Live device count combined with panel-derived demographic weights to model the total audience.",
  },
  MODEL: {
    label: "MODEL",
    tone: "warning",
    helper: "Statistical estimate — not directly measured. Excluded from certificates and rankings.",
    long: "Modelled estimate from partial signal or cross-market inference. Provided for planning reference only.",
  },
  MANUAL: {
    label: "MANUAL",
    tone: "muted",
    helper: "Human-entered figure. Admin/support only — never counted in public totals.",
    long: "Manually entered value from station operator or WaveScorePro support. Display-only.",
  },
};

// Market-level ribbons — the big banner across the top of a station page
export const MARKET_RIBBONS = {
  calibrated: {
    label: "INDEPENDENT MEASUREMENT",
    tone: "success",
    helper: "Anchored to GSS 2021 PHC + GLSS-7. Panel-corrected.",
  },
  modelled: {
    label: "MODELLED ESTIMATE",
    tone: "warning",
    helper: "Modelled estimate — not calibrated to census. Excluded from rankings and certificates.",
  },
  coming_soon: {
    label: "COMING SOON",
    tone: "neutral",
    helper: "Not yet measured. Browsable only; no audience numbers shown.",
  },
};

// JSON-LD schema.org phrasing — used in <script type="application/ld+json">
// tags and in OG description meta tags. All phrases MUST be honest about
// scope (never claim "national" or "audit-grade" unless PANEL data exists).
export const JSONLD_PHRASING = {
  site_description: ("Real-time audience measurement for West African radio, "
                     + "anchored to Ghana Statistical Service (GSS) 2021 PHC + GLSS-7. "
                     + "Independent, automated, methodology transparent."),
  org_slogan: "West Africa Audience Intelligence",
  audience_estimate_scope: ("Estimate produced by automated measurement and "
                            + "statistical modelling. Data source and confidence "
                            + "level shown on every figure. Not audit-grade."),
  ranking_scope_template: "Rank #{rank} of {pool_size} streaming stations in {country}",
};

// Universal PDF/report footer disclaimer
export const PDF_DISCLAIMER = (
  "Figures in this report are estimates produced by automated measurement and "
  + "statistical modelling, labelled with their data source and confidence level. "
  + "They are provided for planning reference only — not audit-grade measurement, "
  + "and not intended for legal or regulatory reporting. "
  + "Full methodology: wavescorepro.com/methodology"
);

// ─────────────────────────────────────────────────────────────────────────
// Phase-1 accuracy vocabulary — added Feb 2026 during the copy-accuracy sweep.
//
// WHY: a TCP/stream connection is not a headcount. One person on three devices
// = 3 connections; a stream left playing to an empty room = 1; reconnect churn
// inflates. Every NUMERIC audience figure now labels its unit as "connections"
// (or "live connections") with an inference tooltip; aggregate prose (e.g.
// "8,342 listening online") is still allowed.
// ─────────────────────────────────────────────────────────────────────────
export const UNIT_VOCAB = {
  unit_singular: "connection",
  unit_plural: "connections",
  unit_live: "live connections",
  unit_long: "concurrent digital connections",
  unit_inference_tooltip: (
    "We count concurrent connections to a station's stream and platform "
    + "APIs. One listener may use more than one device, and a connection "
    + "may continue after someone stops listening. This is a live digital "
    + "connection count — not a survey headcount."
  ),
  unit_inference_short: "Live digital connection count — not a survey headcount.",
};

export const COVERAGE_VOCAB = {
  coverage_line_template: (
    "Measuring {measured} of {total_known} tracked {country} stations "
    + "currently publishing a live digital count · "
    + "rankings cover measured stations only."
  ),
  rank_scope_template: (
    "Digital connection rank · {rank} of {measured} measured · "
    + "as of {as_of} GMT"
  ),
  stations_publishing_template: "{count} stations publishing live counts",
  not_measured_cta: "Not currently measured",
  not_measured_operator_cta: "Run this station? Get measured →",
};

export const PHASE2_VOCAB = {
  phase2_prefix: "Phase 2 —",
  phase2_representative_layer_arriving: "Arriving with our representative layer (Phase 2).",
  phase2_not_yet_published: (
    "Phase 2 — not yet published. Requires our representative sampling "
    + "layer, which is under construction."
  ),
  phase2_pricing_banner: (
    "Phase 1 publishes directly-measured digital connection counts and "
    + "ad-verification certificates. Demographic, panel and representative-"
    + "reach features are marked Phase 2 and are not yet published."
  ),
};

export function coverageLine(measured, totalKnown, country = "Ghanaian") {
  return COVERAGE_VOCAB.coverage_line_template
    .replace("{measured}", measured)
    .replace("{total_known}", totalKnown)
    .replace("{country}", country);
}

export function rankScopeCaption(rank, measured, asOf) {
  return COVERAGE_VOCAB.rank_scope_template
    .replace("{rank}", rank)
    .replace("{measured}", measured)
    .replace("{as_of}", asOf);
}

/** Return the canonical row for a data-source chip. Falls back to MODEL when
 *  the caller has an unknown/legacy value (never crash, never lie). */
export function labelFor(kind, defaultKey = "MODEL") {
  return DATA_SOURCE_LABELS[(kind || "").toUpperCase()] || DATA_SOURCE_LABELS[defaultKey];
}

/** Return the market ribbon row. Unknown markets fall back to 'coming_soon'. */
export function ribbonFor(marketStatus) {
  return MARKET_RIBBONS[marketStatus] || MARKET_RIBBONS.coming_soon;
}

/** Compact tone → hex/CSS colour map (frontend-only concern).
 *  Kept here so consumers get the correct colour + label from ONE lookup. */
export const TONE_COLORS = {
  success: "#10B981",   // green — DIRECT / PANEL / INDEPENDENT
  info:    "#06B6D4",   // cyan  — HYBRID
  warning: "#F59E0B",   // amber — MODEL / MODELLED
  neutral: "#94A3B8",   // slate — COMING SOON
  muted:   "#64748B",   // slate-muted — MANUAL
};

// F-115 + Phase-1 gate — labels that are ALWAYS safe to publish.
// Kept in-file (not fetched from the flag endpoint) so that early renders
// before the /phase call resolves default to the conservative Phase-1 set.
export const PHASE1_ALLOWED_LABELS = ["DIRECT", "MANUAL"];
export const PHASE2_ALLOWED_LABELS = ["DIRECT", "PANEL", "HYBRID", "MODEL", "MANUAL"];
